import { Decree } from "@/types/decree.type";
import { ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BookIcon, CloseIcon, CopyIcon } from "components/icons";
import { isEmpty, isNil } from "helper/utils";

import { Button } from "@/components/ui/button";
import { CurbZone } from "types/curb-zone.type";
import { PolicySummarySource, formatPolicySummary } from "helper/policy";
import { distanceUnit } from "constants/mapbox.const";
import { formatDate } from "helper/date-time";
import { numberFormat } from "helper/format-data";
import { renderTextWithLineBreaks } from "helper/react-utils";
import useToggle from "hooks/use-toggle";
import { StickyNote } from "lucide-react";

type CurbZoneDetailProps = {
  curbZone: CurbZone;
  resetState: () => void;
  openCurbZoneModal: React.Dispatch<React.SetStateAction<boolean>>;
  cityId: string;
  handleRemoveCurbZone: (curbZoneId: string) => void;
  copyCurbZone: () => void;
  title: string | null;
};

export const CurbZoneDetail = ({
  resetState,
  curbZone,
  openCurbZoneModal,
  handleRemoveCurbZone,
  copyCurbZone,
  title,
}: CurbZoneDetailProps) => {
  const [openConfirm, setOpenConfirm] = useToggle();

  const removeParking = () => {
    if (curbZone.id) {
      handleRemoveCurbZone(curbZone.id);
    }
  };

  const handleCloseConfirm = () => {
    setOpenConfirm();
  };

  const handleOpenConfirm = () => {
    setOpenConfirm();
  };
  return (
    <div className="transition-opacity absolute h-auto top-36 bg-primary p-3 right-6 rounded-md shadow-lg z-5 w-100 max-h-[calc(100vh-10rem)] overflow-y-auto">
      <div className="flex justify-between h-6 w-full">
        <CopyIcon className="text-white w-4 h-4 cursor-pointer" onClick={copyCurbZone} />
        {title && <p className="leading-5 text-lg font-semibold text-white">{title}</p>}

        <CloseIcon className="text-white w-4 h-4 cursor-pointer" onClick={resetState} />
      </div>

      <div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Policy</span>
          <div className="flex flex-wrap gap-2 ml-8">
            {curbZone?.curbPolicies && <ListPolicyOrDecree policies={curbZone.curbPolicies} type="policy" />}
          </div>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Decree</span>
          <div className="flex flex-wrap gap-2 ml-8 grow max-w-full justify-end">
            {curbZone?.decrees && <ListPolicyOrDecree policies={curbZone.decrees} type="decree" />}
          </div>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Forme</span>
          <span className="text-sm text-secondary">{curbZone?.parkingAngle}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Nom voie</span>
          <span className="text-sm text-secondary">{curbZone?.streetName}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Début du numéro de rue</span>
          <span className="text-sm text-secondary">{curbZone?.streetNumberStart}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Fin du numéro de rue</span>
          <span className="text-sm text-secondary">{curbZone?.streetNumberEnd}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Longueur</span>
          <span className="text-sm text-secondary">{`${numberFormat(curbZone?.length)} ${distanceUnit}`}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Largeur</span>
          <span className="text-sm text-secondary">{`${numberFormat(curbZone?.width)} ${distanceUnit}`}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Surface calculée</span>
          <span className="text-sm text-secondary">{numberFormat(curbZone?.calculatedArea)}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Places calculées</span>
          <span className="text-sm text-secondary">{numberFormat(curbZone?.calculatedSpaces)}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Date relevé</span>
          <span className="text-sm text-secondary">{formatDate(curbZone.publishedDate)}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Start date</span>
          <span className="text-sm text-secondary">{formatDate(curbZone.startDate)}</span>
        </div>
        {!isNil(curbZone?.externalId) && !isEmpty(curbZone?.externalId) && (
          <div className="flex justify-between w-full items-center py-2">
            <span className="text-sm text-secondary">Identifiant externe</span>
            <span className="text-sm text-secondary">{curbZone?.externalId}</span>
          </div>
        )}
        {!isNil(curbZone?.note) && !isEmpty(curbZone?.note) && (
          <div className="flex justify-between w-full items-center py-2">
            <span className="text-sm text-secondary">Commentaire</span>
            <div className="text-sm text-secondary">{renderTextWithLineBreaks(curbZone?.note)}</div>
          </div>
        )}
      </div>
      <div className="flex justify-between mt-4">
        <Button onClick={handleOpenConfirm} variant="destructive">
          Delete
        </Button>
        <Button onClick={() => openCurbZoneModal(true)} variant="secondary">
          Edit
        </Button>
      </div>
      <AlertDialog open={openConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete a curb zone</AlertDialogTitle>
            <AlertDialogDescription>Are you sure that you want to delete this curb zone?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseConfirm}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeParking}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

type ListType = "decree" | "policy";
type PolicyListItem = { name: string } & PolicySummarySource;
type ListPolicyOrDecreeProps =
  | { type: "policy"; policies: PolicyListItem[] }
  | { type: "decree"; policies: Pick<Decree, "name">[] };

const ListPolicyOrDecree = (props: ListPolicyOrDecreeProps) => {
  if (!props.policies || props.policies.length === 0) {
    return (
      <span className="h-5 flex-1 truncate text-left text-secondary text-sm flex justify-end">
        No {props.type} yet!
      </span>
    );
  }

  if (props.type === "policy") {
    return (
      <>
        {props.policies.map((item, index) => (
          <RegulationCard
            key={`regulation-${index}`}
            type="policy"
            name={item.name}
            summary={formatPolicySummary(item)}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {props.policies.map((item, index) => (
        <RegulationCard key={`regulation-${index}`} type="decree" name={item.name} />
      ))}
    </>
  );
};

const RegulationCard = ({ type, name, summary }: { type: ListType; name: string; summary?: string }) => (
  <div className="flex items-center max-w-full">
    <div className="regulation-card flex items-center min-w-0 mb-2 w-full text-sm text-secondary">
      <div className="flex flex-col items-center justify-center rounded-full bg-active size-[29px]">
        {iconByType[type]}
      </div>
      <div className="flex flex-col">
        <span>{name}</span>
        {summary && <span className="text-xs opacity-80">{summary}</span>}
      </div>
    </div>
  </div>
);

const iconByType: Record<ListType, ReactNode> = {
  policy: <BookIcon className="w-4 h-4 mr-3" />,
  decree: <StickyNote className="size-4 mr-3 flex-none" />,
};
